import { useEffect, useRef, useState } from 'react';
import {
  PALETTE, MIC_TYPE_LABEL, PRESENTER_ICONS, minutesToHHMM, hhmmToMinutes,
  type MicType, type PresenterIcon, type TimeSlot,
} from '@micplot/shared';
import { api } from '../lib/api';
import { useStore, usePresenters } from '../state/store';
import ConfirmDialog from './ConfirmDialog';
import Avatar, { ICON_GLYPH } from './Avatar';
import { downscaleToDataUrl } from '../lib/image';

interface Props {
  presenterId: string;
  onClose: () => void;
  onNavigate: (id: string) => void;
}

export default function PresenterEditor({ presenterId, onClose, onNavigate }: Props) {
  const identity = useStore(s => s.identity)!;
  const notify = useStore(s => s.notify);
  const presenters = usePresenters();
  const presenter = presenters.find(p => p.id === presenterId);

  const [tab, setTab] = useState<'details' | 'times' | 'photo'>('details');
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [name, setName] = useState('');
  const [characterName, setCharacterName] = useState('');
  const [icon, setIcon] = useState<PresenterIcon | ''>('');
  const [title, setTitle] = useState('');
  const [color, setColor] = useState(0);
  const [mic, setMic] = useState<MicType>('lav');
  const [slots, setSlots] = useState<{ start: string; end: string }[]>([]);

  // Reload the form whenever we page to a different presenter.
  useEffect(() => {
    if (!presenter) return;
    setName(presenter.name);
    setCharacterName(presenter.characterName ?? '');
    setIcon(presenter.icon ?? '');
    setTitle(presenter.title ?? '');
    setColor(presenter.color);
    setMic(presenter.micPreference);
    setSlots(presenter.slots.map(s => ({
      start: minutesToHHMM(s.start), end: minutesToHHMM(s.end),
    })));
  }, [presenterId, presenter]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!presenter) return null;

  const idx = presenters.findIndex(p => p.id === presenterId);

  async function save() {
    const parsed: Pick<TimeSlot, 'start' | 'end'>[] = [];
    for (const s of slots) {
      const start = hhmmToMinutes(s.start);
      const end = hhmmToMinutes(s.end);
      if (start === null || end === null) {
        notify('Times must look like 14:30.', 'error');
        return;
      }
      if (end <= start) {
        notify(`${s.start}–${s.end} ends before it starts.`, 'error');
        return;
      }
      parsed.push({ start, end });
    }
    try {
      await api.updatePresenter(identity.code, presenterId, {
        name: name.trim() || presenter!.name,
        characterName: characterName.trim(),
        icon: icon || '',
        title: title.trim(),
        color,
        micPreference: mic,
        slots: parsed as TimeSlot[],
      });
      onClose();
    } catch (err) {
      notify((err as Error).message, 'error');
    }
  }

  async function upload(file: File) {
    setUploading(true);
    try {
      const dataUrl = await downscaleToDataUrl(file);
      await api.setHeadshot(identity.code, presenterId, dataUrl);
      notify('Head shot updated.');
    } catch (err) {
      notify((err as Error).message, 'error');
    } finally {
      setUploading(false);
    }
  }

  async function remove() {
    setConfirmDelete(false);
    try {
      await api.deletePresenter(identity.code, presenterId);
      onClose();
    } catch (err) {
      notify((err as Error).message, 'error');
    }
  }

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-label="Edit presenter">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2>Edit presenter</h2>
          <div className="row">
            <button className="btn sm" disabled={idx <= 0}
                    onClick={() => onNavigate(presenters[idx - 1]!.id)}>‹</button>
            <button className="btn sm" disabled={idx >= presenters.length - 1}
                    onClick={() => onNavigate(presenters[idx + 1]!.id)}>›</button>
            <button className="btn sm" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>
        <p className="sub">{idx + 1} of {presenters.length}</p>

        <div className="tabs" role="tablist">
          <button role="tab" aria-selected={tab === 'details'}
                  onClick={() => setTab('details')}>Details</button>
          <button role="tab" aria-selected={tab === 'times'}
                  onClick={() => setTab('times')}>
            Time slots {slots.length > 0 && `(${slots.length})`}
          </button>
          <button role="tab" aria-selected={tab === 'photo'}
                  onClick={() => setTab('photo')}>
            Head shot
          </button>
        </div>

        {tab === 'details' ? (
          <>
            <div className="field">
              <label htmlFor="pname">Name</label>
              <input id="pname" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="pchar">Character / instrument</label>
              <input id="pchar" value={characterName}
                     onChange={e => setCharacterName(e.target.value)}
                     placeholder="e.g. Ophelia, Lead Guitar, Podium 2" />
              <div className="hint">
                What they are on stage. The deck calls people by one name and
                production calls them by the other, so both are shown.
              </div>
            </div>
            <div className="field">
              <label htmlFor="ptitle">Role / title</label>
              <input id="ptitle" value={title} onChange={e => setTitle(e.target.value)}
                     placeholder="e.g. CEO, Moderator" />
            </div>
            <div className="field">
              <label>Colour</label>
              <div className="swatches">
                {PALETTE.map((hex, i) => (
                  <button key={hex} style={{ background: hex }} aria-pressed={color === i}
                          aria-label={`Colour ${i + 1}`} onClick={() => setColor(i)} />
                ))}
              </div>
            </div>
            <div className="field">
              <label htmlFor="pmic">Mic preference</label>
              <select id="pmic" value={mic} onChange={e => setMic(e.target.value as MicType)}>
                {(Object.keys(MIC_TYPE_LABEL) as MicType[]).map(t => (
                  <option key={t} value={t}>{MIC_TYPE_LABEL[t]}</option>
                ))}
              </select>
              <div className="hint">
                Body-worn mics need changeover time between presenters; handhelds don't.
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <span className="muted" style={{ fontSize: 13 }}>When they're on stage.</span>
              <button className="btn sm"
                      onClick={() => setSlots([...slots, { start: '09:00', end: '09:30' }])}>
                + Add time
              </button>
            </div>
            {slots.length === 0 && (
              <p className="muted" style={{ fontSize: 13 }}>
                No times set. Without them this presenter won't appear on the timeline
                and auto-assign will skip them.
              </p>
            )}
            {slots.map((s, i) => (
              <div className="row" key={i} style={{ marginBottom: 8 }}>
                <input value={s.start} placeholder="09:00" aria-label="Start"
                       onChange={e => setSlots(slots.map((x, j) =>
                         j === i ? { ...x, start: e.target.value } : x))} />
                <span className="muted">to</span>
                <input value={s.end} placeholder="09:30" aria-label="End"
                       onChange={e => setSlots(slots.map((x, j) =>
                         j === i ? { ...x, end: e.target.value } : x))} />
                <button className="btn sm" aria-label="Remove time"
                        onClick={() => setSlots(slots.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
          </>
        )}

        {tab === 'photo' && (
          <>
            <div
              className="dropzone"
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) upload(f);
              }}
              onClick={() => fileRef.current?.click()}
            >
              {presenter.headshotUrl ? (
                <img src={presenter.headshotUrl} alt={`${presenter.name} head shot`} />
              ) : icon ? (
                <Avatar presenter={{ ...presenter, icon }} size={96} />
              ) : (
                <>
                  <div style={{ fontWeight: 600 }}>Click or drag an image here</div>
                  <div className="hint">JPEG, PNG or WebP · up to 5MB</div>
                </>
              )}
            </div>
            <input
              ref={fileRef} type="file" hidden accept="image/jpeg,image/png,image/webp"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) upload(f);
                e.target.value = '';
              }}
            />
            <div className="row" style={{ marginTop: 10 }}>
              <button className="btn" onClick={() => cameraRef.current?.click()}>
                Take photo
              </button>
              <button className="btn" onClick={() => fileRef.current?.click()}>
                Choose file
              </button>
            </div>
            {/* capture= opens the camera straight away on a phone, which is how
                these actually get taken: someone grabbed in the corridor. */}
            <input
              ref={cameraRef} type="file" hidden accept="image/*" capture="environment"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) upload(f);
                e.target.value = '';
              }}
            />
            <div className="hint" style={{ marginTop: 8 }}>
              {uploading
                ? 'Uploading…'
                : 'Images are resized in your browser before upload to keep the show file small.'}
            </div>

            <div className="field" style={{ marginTop: 18 }}>
              <label>Or pick an icon</label>
              <div className="hint" style={{ marginBottom: 8 }}>
                Used when there is no photo — faster than finding one, and still
                readable at a glance.
              </div>
              <div className="icon-grid">
                <button className={`icon-btn${icon === '' ? ' on' : ''}`}
                        onClick={() => setIcon('')} title="No icon — use initials">
                  {initialsOf(name)}
                </button>
                {PRESENTER_ICONS.map(ic => (
                  <button key={ic} className={`icon-btn${icon === ic ? ' on' : ''}`}
                          onClick={() => setIcon(ic)} title={ic}>
                    {ICON_GLYPH[ic]}
                  </button>
                ))}
              </div>
              <div className="hint" style={{ marginTop: 8 }}>
                Remember to Save — the icon applies when you do.
              </div>
            </div>
            {presenter.headshotUrl && (
              <button className="btn sm" style={{ marginTop: 10 }} onClick={async () => {
                try {
                  await api.deleteHeadshot(identity.code, presenterId);
                } catch (err) { notify((err as Error).message, 'error'); }
              }}>
                Remove photo
              </button>
            )}
          </>
        )}

        <div className="modal-actions">
          <button className="btn danger" onClick={() => setConfirmDelete(true)}>Delete</button>
          <div className="header-spacer" />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={save}>Save</button>
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title={`Delete ${presenter.name}?`}
          body="This also clears their channel assignments. It cannot be undone."
          confirmLabel="Delete"
          danger
          onConfirm={remove}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts.at(-1)![0]!).toUpperCase();
}
